import { MEErrorService } from "../../error/me-error.service";

export class MEApiUtility {

  static srvError: MEErrorService;

  static init(srvError: MEErrorService) {
    this.srvError = srvError;
  }

  static isSuccess(status: number): boolean {
    return status >= 200 && status < 300;
  }

  static isHttpError(status: number): boolean {
    return !this.isSuccess(status);
  }

  /**
   * Match HTTP status code (Angular HttpClient or CapacitorHttp response)
   */
  static getHttpErrorMessage(status?: number): string {
    if (!status) {
      return 'Unknown HTTP error.';
    }

    return (
      ME_HTTP_ERROR_MAP.get(status)?.message ??
      `Unexpected server error (${status}).`
    );
  }

  /**
   * Match CapacitorHttp catch() error
   * Accepts raw error object and normalizes it
   */
  static getCapacitorErrorMessage(error: any): string {
    const key = MEApiUtility.resolveCapacitorErrorKey(error);
    return (
      ME_CAPACITOR_ERROR_MAP.get(key)?.message ??
      ME_CAPACITOR_ERROR_MAP.get('UNKNOWN')!.message
    );
  }

  /**
   * Normalize Capacitor error into a known key
   */
  private static resolveCapacitorErrorKey(error: any): string {
    if (!error) return 'UNKNOWN';

    const msg = (error.message || '').toLowerCase();

    if (msg.includes('timeout')) return 'TIMEOUT';
    if (msg.includes('network')) return 'NETWORK_ERROR';
    if (msg.includes('ssl')) return 'SSL_ERROR';
    if (msg.includes('abort')) return 'ABORTED';
    if (msg.includes('offline')) return 'OFFLINE';

    return 'UNKNOWN';
  }

  public static showHttpErrDialog(status: number) {
    let statusMsg: string = 'HTTP Status: ' + status;
    let errorMsg: string = '. Message: ' + MEApiUtility.getHttpErrorMessage(status);
    let msg: string = status ? statusMsg + errorMsg : errorMsg;
    this.srvError.showDialog("FAILURE_CREATING_THE_REQUEST", [msg]);
  }

  public static showCapacitorErrDialog(error: any) {
    this.srvError.showDialog("FAILURE_CREATING_THE_REQUEST", [MEApiUtility.getCapacitorErrorMessage(error)]);
  }
}

export interface MEHttpError {
  code: number;
  message: string;
}

/**
 * Universal HTTP error map
 * Works for:
 * - Angular HttpClient (HttpErrorResponse.status)
 * - CapacitorHttp response.status
 */
export const ME_HTTP_ERROR_MAP = new Map<number, MEHttpError>([
  // 4xx Client Errors
  [400, { code: 400, message: 'Bad request' }],
  [401, { code: 401, message: 'Unauthorized. Please log in again.' }],
  [403, { code: 403, message: 'Forbidden. You do not have access.' }],
  [404, { code: 404, message: 'Not found.' }],
  [408, { code: 408, message: 'Request timeout.' }],
  [409, { code: 409, message: 'Conflict occurred.' }],
  [422, { code: 422, message: 'Unprocessable entity.' }],
  [429, { code: 429, message: 'Too many requests. Please try again later.' }],

  // 5xx Server Errors
  [500, { code: 500, message: 'Internal server error.' }],
  [502, { code: 502, message: 'Bad gateway.' }],
  [503, { code: 503, message: 'Service unavailable.' }],
  [504, { code: 504, message: 'Gateway timeout.' }],
]);

export interface MECapacitorError {
  key: string;
  message: string;
}

/**
 * Errors caught by CapacitorHttp.catch()
 * status code does NOT exist here
 */
export const ME_CAPACITOR_ERROR_MAP = new Map<string, MECapacitorError>([
  ['NETWORK_ERROR', { key: 'NETWORK_ERROR', message: 'Network connection failed.' }],
  ['TIMEOUT', { key: 'TIMEOUT', message: 'Request timed out.' }],
  ['ABORTED', { key: 'ABORTED', message: 'Request was aborted.' }],
  ['SSL_ERROR', { key: 'SSL_ERROR', message: 'Secure connection failed.' }],
  ['OFFLINE', { key: 'OFFLINE', message: 'No internet connection.' }],
  ['UNKNOWN', { key: 'UNKNOWN', message: 'Unexpected network error.' }],
]);
